import { FC, useCallback, useState } from "react";
import Input from "@/components/input";
import styles from "./SearchProductByName.module.scss";
import { Button } from "@/components/button";
import { Product } from "@/types/product";
import { filterProductsBySearchInput } from "@/helpers/products";
import { useTranslation } from "react-i18next";
import { logFirebaseEvent } from "@/services/firebase";
import debounce from "@/helpers/debounce";
import { playSound, SoundOption } from "@/helpers/playSound";
import { useAppSelector } from "@/hooks/useAppSelector";
import { selectProducts } from "@/store/product/selectors";

interface SearchProductByNameProps {
  setFilteredProducts: (products: Product[] | null) => void;
}

const SearchProductByName: FC<SearchProductByNameProps> = ({ setFilteredProducts }) => {
  const [value, setValue] = useState("");
  const { t } = useTranslation();
  const { products } = useAppSelector(selectProducts);

  const debounceWrapper = useCallback(
    debounce((value: string) => {
      logFirebaseEvent("search", { search_term: value });
    }, 5000),
    []
  );

  const onValueChange = (value: string) => {
    setValue(value);
    if (value.length < 2) return setFilteredProducts(null);
    if (!products) return;
    setFilteredProducts(filterProductsBySearchInput(products, value));
    debounceWrapper(value);
  };

  const onClick = () => {
    playSound(SoundOption.Click1);
    logFirebaseEvent("search_button_pressed", { search_term: value });
  };

  const onRemoveClick = () => {
    playSound(SoundOption.Click2);
    logFirebaseEvent("clear_search_button_pressed");
  };

  return (
    <div className={styles.grid}>
      <Input
        placeholder={t("mainPage.typeProduct")}
        containerClassName={styles.input}
        onValueChange={onValueChange}
        value={value}
        type="text"
        data-cy="search-product-input"
        onRemoveClick={onRemoveClick}
      />
      <Button text={t("ui.search")} className={styles.button} size="x-small" onClick={onClick} />
    </div>
  );
};

export default SearchProductByName;
